import React, { useState } from 'react';
import {
  Chip,
  Menu,
  MenuItem,
} from '@mui/material';
import { OutlinedIcons } from '@marageti/z4-lib';
import TicketCloseModal, { TicketCloseFormData } from '../modals/ticket-close-modal';
import { STATUS_DISPLAY_LABELS, STATUS_MENU_OPTIONS } from '../../pages/tickets/options';

const { OutlinedSelectorIcon } = OutlinedIcons;

const STATUS_CHIP_COLOR: Record<
string,
'default' | 'success' | 'warning' | 'error' | 'info' | 'primary' | 'secondary'
> = {
  PENDING: 'default',
  WAITING_ON_TRAVELER_CARE: 'info',
  WAITING_ON_PARTNERSHIP: 'warning',
  COMPLETED: 'primary',
  CANCELED: 'error',
};

export type TicketStatusChipProps = {
  status: string;
  onStatusChange: (status: string) => void;
  onCloseTicket?: (data: TicketCloseFormData) => Promise<void>;
};

const TicketStatusChip = ({ status, onStatusChange, onCloseTicket }: TicketStatusChipProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const menuOpen = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleSelect = (value: string) => {
    setAnchorEl(null);
    if (value === status) return;

    if (value === 'COMPLETED' && onCloseTicket) {
      setCloseModalOpen(true);
    } else {
      onStatusChange(value);
    }
  };

  const handleCloseTicket = async (data: TicketCloseFormData) => {
    if (onCloseTicket) {
      await onCloseTicket(data);
    }
    setCloseModalOpen(false);
  };

  return (
    <>
      <Chip
        label={STATUS_DISPLAY_LABELS[status as keyof typeof STATUS_DISPLAY_LABELS] || status}
        color={STATUS_CHIP_COLOR[status] || 'default'}
        deleteIcon={<OutlinedSelectorIcon sx={{ fontSize: 18 }} />}
        onDelete={handleClick}
        onClick={handleClick}
      />
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
        onClick={(e) => e.stopPropagation()}
      >
        {STATUS_MENU_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            disabled={option.value === status}
            onClick={() => handleSelect(option.value)}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
      {onCloseTicket && (
        <TicketCloseModal
          open={closeModalOpen}
          onClose={() => setCloseModalOpen(false)}
          onSave={handleCloseTicket}
        />
      )}
    </>
  );
};

export default TicketStatusChip;
